import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";

export default function Navbar() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { href: "#camere", label: t.nav.camere, key: "camere" },
    { href: "#galleria", label: t.gallery.overline, key: "galleria" },
    { href: "#ristorante", label: t.nav.ristorante, key: "ristorante" },
    { href: "#contatti", label: t.nav.contatti, key: "contatti" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAF9F5]/90 backdrop-blur-md border-b border-[#E2E0D9]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between gap-6">
        <a
          href="#top"
          data-testid="brand-logo"
          onClick={(e) => handleClick(e, "#top")}
          className="flex items-baseline gap-2 shrink-0"
        >
          <span className="font-serif-display text-2xl md:text-3xl text-[#1A1C19] tracking-tight">
            Novi <span className="italic text-[#A88B7D]">Hotel</span>
          </span>
        </a>

        <ul className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <li key={l.key}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.key}`}
                onClick={(e) => handleClick(e, l.href)}
                className="text-xs uppercase tracking-[0.22em] text-[#1A1C19] hover:text-[#A88B7D] transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-5">
          <LanguageSwitcher variant="desktop" />
          <a
            href="#contatti"
            data-testid="nav-cta-book"
            onClick={(e) => handleClick(e, "#contatti")}
            className="btn-primary"
          >
            {t.nav.prenota}
          </a>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <LanguageSwitcher variant="mobile" />
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-[#1A1C19]"
            aria-label={t.nav.openMenu}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#FAF9F5] border-t border-[#E2E0D9]"
        >
          <ul className="flex flex-col px-6 py-6 gap-5">
            {links.map((l) => (
              <li key={l.key}>
                <a
                  href={l.href}
                  data-testid={`mobile-nav-${l.key}`}
                  onClick={(e) => handleClick(e, l.href)}
                  className="text-sm uppercase tracking-[0.22em] text-[#1A1C19]"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li>
              <a
                href="#contatti"
                onClick={(e) => handleClick(e, "#contatti")}
                className="btn-primary w-full justify-center mt-2"
              >
                {t.nav.prenota}
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
